import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, Clock, Users, CalendarPlus, Star, MapPin, Zap, BookmarkCheck, Bookmark, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import ReliabilityBadge from "@/components/ReliabilityBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PartnerMiniCard({ partner, onToggleSave, onPlayAgain, isSaved }) {
  const initials = partner.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const rb = partner.reliability_badge;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-green/30 transition-all group" data-testid="partner-mini-card">
      <div className="flex items-start gap-3">
        <Link to={`/profile/${partner.id}`} className="flex-shrink-0">
          {partner.avatar_url ? (
            <img src={partner.avatar_url} alt={partner.name} className="w-12 h-12 rounded-xl object-cover ring-2 ring-transparent group-hover:ring-brand-green/30 transition-all" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green font-black font-heading">
              {initials}
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Link to={`/profile/${partner.id}`} className="font-bold text-brand-navy text-sm hover:text-brand-green transition-colors">
                {partner.name}
              </Link>
              <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                <MapPin size={10} /> <span className="truncate">{partner.city || "Fort Worth, TX"}</span>
              </div>
            </div>
            <button
              onClick={() => onToggleSave(partner.id, isSaved)}
              className="flex-shrink-0 p-1 rounded-lg hover:bg-brand-green/10 transition-colors"
              aria-label={isSaved ? "Unsave partner" : "Save partner"}
              data-testid={isSaved ? "unsave-partner-btn" : "save-partner-btn"}
            >
              {isSaved
                ? <BookmarkCheck size={15} className="text-brand-green" />
                : <Bookmark size={15} className="text-gray-400" />}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {rb && <ReliabilityBadge badge={rb.badge} label={rb.label} size="xs" />}
            {partner.last_session_sport && (
              <span className="text-xs text-gray-400 capitalize">{partner.last_session_sport}</span>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={() => onPlayAgain(partner)}
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-bold border border-brand-orange/40 text-brand-orange rounded-lg hover:bg-brand-orange/10 transition-colors"
        data-testid="play-again-btn"
      >
        <RefreshCw size={11} /> Play Again
      </button>
    </div>
  );
}

export default function PartnersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [tab, setTab] = useState("saved");
  const [saved, setSaved] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [sv, rc] = await Promise.all([
        axios.get(`${API}/partners/saved`, { withCredentials: true }),
        axios.get(`${API}/partners/recent`, { withCredentials: true }),
      ]);
      setSaved(sv.data.partners);
      setRecent(rc.data.partners);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const handleToggleSave = async (partnerId, isCurrentlySaved) => {
    try {
      if (isCurrentlySaved) {
        await axios.delete(`${API}/partners/${partnerId}/save`, { withCredentials: true });
        setSaved(prev => prev.filter(p => p.id !== partnerId));
        showToast("Partner removed from saved.");
      } else {
        await axios.post(`${API}/partners/${partnerId}/save`, {}, { withCredentials: true });
        showToast("Partner saved!");
        load();
      }
    } catch {
      showToast("Failed to update saved partners.");
    }
  };

  const handlePlayAgain = (partner) => {
    navigate(`/profile/${partner.id}?playagain=1`);
  };

  const list = tab === "saved" ? saved : recent;
  const savedIds = new Set(saved.map(p => p.id));

  const TABS = [
    { id: "saved", label: "Saved Partners", icon: Heart, count: saved.length },
    { id: "recent", label: "Recent Partners", icon: Clock, count: recent.length },
  ];

  return (
    <div className="min-h-screen bg-brand-navy pt-20 pb-16 px-4 dark-section" data-testid="partners-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-black text-white mb-2">
            Your Partners
          </h1>
          <p className="text-gray-400 text-sm">Save players you enjoy playing with and request rematches.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#141820] rounded-xl p-1 mb-6 w-fit dark-section">
          {TABS.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === id
                  ? "bg-brand-green text-white shadow"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid={`partners-tab-${id}`}
            >
              <Icon size={13} />
              {label}
              {count > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${tab === id ? "bg-white/20" : "bg-gray-700 text-gray-300"}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading partners…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-[#1A1F2E] border border-gray-200/10 flex items-center justify-center mx-auto mb-4 dark-section">
              {tab === "saved" ? <Heart size={24} className="text-gray-500" /> : <Clock size={24} className="text-gray-500" />}
            </div>
            <p className="text-gray-400 text-sm mb-4">
              {tab === "saved"
                ? "You haven't saved any partners yet."
                : "No completed sessions yet."}
            </p>
            <Link
              to="/find-partners"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white text-sm font-bold rounded-lg hover:bg-brand-orange/90 transition-colors"
              data-testid="find-partners-cta"
            >
              <Users size={14} /> Find Partners
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {list.map(partner => (
              <PartnerMiniCard
                key={partner.id}
                partner={partner}
                isSaved={savedIds.has(partner.id)}
                onToggleSave={handleToggleSave}
                onPlayAgain={handlePlayAgain}
              />
            ))}
          </div>
        )}

        {/* Invite prompt */}
        <div className="mt-10 bg-[#1A1F2E] border border-brand-green/20 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 dark-section">
          <div className="flex-1">
            <p className="text-white font-bold text-sm">Don't see enough partners?</p>
            <p className="text-gray-400 text-xs mt-0.5">Invite friends to join and grow your network.</p>
          </div>
          <Link
            to="/invite"
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded-lg hover:bg-brand-green/90 transition-colors whitespace-nowrap"
            data-testid="invite-friends-btn"
          >
            <Zap size={13} /> Invite Friends
          </Link>
        </div>
      </div>
    </div>
  );
}
